import clone from 'lodash/clone';
import find from 'lodash/find';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import {
    Content, IContentOptions, vector3Json, ContentType
} from './content';
import { SystemEvent } from '../System/system';
import { SystemLayer } from '../System/systemLayer';
import { projectInterface } from '../Tool/interface';
import { Animation } from '../System/Animation/animation';
import { Ease } from '../System/Animation/animationEasing';

export interface IAnimationOptions {
    name: string,
    event: 'show' | 'hover' | 'enter' | 'click' | 'scroll' | 'mouse',
    curve: 'linear' | 'bezier' | 'circle' | 'exponential' | 'bounce',
    duration?: number,
    way?: 'once' | 'alternate' | 'loop',
    model?: string, // Model animation name if content is a model
    target?: string,
    position?: Vector3,
    rotation?: Vector3,
    scale?: Vector3,
    startposition?: Vector3,
    startrotation?: Vector3,
    startscale?: Vector3,
    delay?: number,
    anim?: Animation,
    modelanim?: AnimationGroup,
    timeout?: any,
    launched?: boolean,
    forcestop?: boolean, // forcestop used in animation edition
    started?: boolean, // forcestop used in animation edition
    finished?: boolean, // forcestop used in animation edition
}

projectInterface.animation = {
    name: 'an',
    event: 'ae',
    duration: 'adu',
    curve: 'ac',
    target: 'at',
    delay: 'ade',
    way: 'aw',
    model: 'am',
    position: {
        dim: 'ap',
        next: vector3Json
    },
    rotation: {
        dim: 'ar',
        next: vector3Json
    },
    scale: {
        dim: 'as',
        next: vector3Json
    }
};

const stopAnimation = (contentAnimation: IAnimationOptions) => {
    contentAnimation.forcestop = true;
    if (contentAnimation.timeout) clearTimeout(contentAnimation.timeout);
    if (contentAnimation.anim) contentAnimation.anim.stop();
};

const setCurveFunction = (contentAnimation: IAnimationOptions) => {
    const { curve } = contentAnimation;
    if (curve === 'linear') contentAnimation.anim.setEasing(Ease.Linear);
    else if (curve === 'bezier') contentAnimation.anim.setEasing(Ease.BezierCurve);
    else if (curve === 'circle') contentAnimation.anim.setEasing(Ease.Circle);
    else if (curve === 'bounce') contentAnimation.anim.setEasing(Ease.Bounce);
    else if (curve === 'exponential') contentAnimation.anim.setEasing(Ease.Exponential);
};

export abstract class ContentAnimation extends Content {
    public animations: any = {};

    private animposition: Vector3;

    private animrotation: Vector3;

    private animscale: Vector3;

    constructor(systemLayer: SystemLayer, content: IContentOptions, contentType: ContentType) {
        super(systemLayer, content, contentType);
        this.animposition = clone(this.position);
        this.animrotation = clone(this.rotation);
        this.animscale = clone(this.scale);

        systemLayer.on(SystemEvent.Start, () => {
            // Make sure every animation works well anytime rendering start
            if (this.play) this.startAllAnimation();
        });
    }

    public setPlay(play: boolean): void {
        this.play = play;
        if (play) this.startAllAnimation();
        else this.pauseAnimation();
    }

    public getAnimations(): void {
        return this.animations;
    }

    public getAnimation(key: string): IAnimationOptions {
        return find(this.animations, (a) => a.name === key);
    }

    public addAnimations(animations: IAnimationOptions[]): void {
        const keys = Object.keys(animations);
        keys.forEach((key) => {
            this.addAnimation(key, animations[key]);
        });
    }

    public startAllAnimation(): void {
        this.resetGeometry();
        const keys = Object.keys(this.animations);
        keys.forEach((key) => {
            const anim = this.animations[key];
            if (anim.event === 'show' || anim.event === 'scroll' || anim.event === 'mouse') {
                this.startAnimation(anim);
            }
        });
    }

    public pauseAnimation(): void {
        const keys = Object.keys(this.animations);
        keys.forEach((key) => {
            const anim = this.animations[key];
            stopAnimation(anim);
        });
        this.resetGeometry();
    }

    public setAnimationParam(key: string, contentAnimation: IAnimationOptions): void {
        // If animation was deleted, we create it
        if (!contentAnimation.event) {
            this.deleteAnimation(key);
        } else {
            if (!this.animations[key]) this.addAnimation(key, contentAnimation);
            const keys = Object.keys(contentAnimation);
            keys.forEach((keyanim) => {
                this.animations[key][keyanim] = contentAnimation[keyanim];
            });
        }
    }

    public deleteAnimation(animkey: string): void {
        if (this.animations[animkey]) stopAnimation(this.animations[animkey]);
        delete this.animations[animkey];
    }

    private infinite = 1000000000000;

    public addAnimation(key: string, cAnim: IAnimationOptions): IAnimationOptions {
        if (cAnim.anim === undefined) cAnim.anim = new Animation(this._system);
        cAnim.launched = false;
        cAnim.finished = false;
        cAnim.started = false;
        if (cAnim.delay === undefined) cAnim.delay = 0;

        stopAnimation(cAnim);
        setCurveFunction(cAnim);
        if (!cAnim.position || !Object.keys(cAnim.position).length) cAnim.position = Vector3.Zero();
        if (!cAnim.rotation || !Object.keys(cAnim.rotation).length) cAnim.rotation = Vector3.Zero();
        if (!cAnim.scale || !Object.keys(cAnim.scale).length) cAnim.scale = Vector3.Zero();

        // FIXME find a way to delete event when animation or content is deleted
        // this.pattern.off('enter', () => {
        //   this.startAnimation(cAnim);
        // });
        this.animations[key] = cAnim;
        if (cAnim.event !== 'show' && cAnim.event !== 'scroll' && cAnim.event !== 'mouse') {
            this.animationEvent(cAnim);
        }
        return cAnim;
    }

    private animationEvent(contentAnimation: IAnimationOptions) {
        if (contentAnimation.event === 'hover') {
            this.pattern.on('enter', () => {
                this.startAnimation(contentAnimation);
            });
            this.pattern.on('leave', () => {
                contentAnimation.anim.stop();
                this.resetGeometry();
            });
        } else if (contentAnimation.event === 'enter') {
            this.pattern.on('enter', () => {
                if (!contentAnimation.launched) this.startAnimation(contentAnimation);
            });
        } else if (contentAnimation.event === 'click') {
            this.pattern.on('click', () => {
                // FIXME BABYLON call twice the click event
                // it's ok but may cause error in the future
                this.startAnimation(contentAnimation);
            });
        }
    }

    // getTarget (id:string) {
    //   let target = find(contentList, (o) => { return o.id == id; });
    //   if (target) return target;
    //   else return this;
    // }

    private playAnimation(contentAnimation: IAnimationOptions) {
        const clonedAnim: IAnimationOptions = clone(contentAnimation);
        clonedAnim.event = 'show';
        clonedAnim.delay = 0;
        // if (clonedAnim.model) clonedAnim.duration = 10;
        // else clonedAnim.duration = 1;
        this.startAnimation(clonedAnim);
    }

    public startAnimation(contentAnimation: IAnimationOptions): void {
        stopAnimation(contentAnimation);
        this.pattern.unOptimize();
        this.finished = false;

        contentAnimation.anim.setParam(contentAnimation.duration * 30); // 30 FPS
        contentAnimation.forcestop = false;
        contentAnimation.finished = false;
        contentAnimation.started = false;

        if (contentAnimation.event !== 'scroll' && contentAnimation.event !== 'mouse') {
            contentAnimation.launched = true;
            this.setStartParam(contentAnimation);
            if (contentAnimation.delay) {
                contentAnimation.timeout = setTimeout(() => {
                    this.animateWay(contentAnimation);
                }, contentAnimation.delay * 1000);
            } else {
                this.animateWay(contentAnimation);
            }
        }
    }

    private setStartParam(contentAnimation: IAnimationOptions) {
        contentAnimation.startposition = clone(this.animposition);
        contentAnimation.startrotation = clone(this.animrotation);
        contentAnimation.startscale = clone(this.animscale);
    }

    // Different animateWay function used in model
    protected animateWay(contentAnimation: IAnimationOptions): void {
        this._animateWay(contentAnimation);
    }

    protected _animateWay(contentAnimation: IAnimationOptions): void {
        const { anim } = contentAnimation;
        if (contentAnimation.way === 'once') {
            contentAnimation.anim.go((perc) => {
                this.animate(contentAnimation, perc);
            }, () => {
                this.finishAnimation(contentAnimation);
            });
        } else if (contentAnimation.way === 'alternate') {
            anim.alternate(this.infinite, anim.howmany, (perc) => {
                this.animate(contentAnimation, perc);
            }, (perc) => {
                this.animate(contentAnimation, 1 - perc);
            });
        } else if (contentAnimation.way === 'loop') {
            anim.loop(this.infinite, anim.howmany, (perc) => {
                this.animate(contentAnimation, perc);
            }, () => {
                if (contentAnimation.delay) this.startAnimation(contentAnimation);
            });
        }
    }

    private scaleAnimationAvailable = true;

    setScaleAnimationAvailable(available: boolean): void {
        this.scaleAnimationAvailable = available;
    }

    // Different resetGeometry function used in model
    public resetGeometry(): void {
        this._resetGeometry();
    }

    protected _resetGeometry(): void {
        this.animposition = clone(this.position);
        this.animrotation = clone(this.rotation);
        this.animscale = clone(this.scale);
        this.pattern.setPosAxis(this.position);
        if (this.scaleAnimationAvailable) this.pattern.setScaleAxis(this.scale);
        this.pattern.setAngleAxis(this.rotation);
    }

    public resetAnimation(contentAnimation: IAnimationOptions): void {
        this.resetGeometry();
        this.addAnimation(contentAnimation.name, contentAnimation);
        if (contentAnimation.event === 'mouse') this.startAnimation(contentAnimation);
        else this.playAnimation(contentAnimation);
    }

    private animate(contentAnimation: IAnimationOptions, perc: number) {
        const positionChange = contentAnimation.position.multiplyByFloats(perc, perc, perc);
        const positionNew = positionChange.add(contentAnimation.startposition);
        ['x', 'y', 'z'].forEach((key) => {
            this.animposition[key] = positionNew[key];
        });
        this.pattern.setPosAxis(positionNew);

        const rotationChange = contentAnimation.rotation.multiplyByFloats(perc, perc, perc);
        const rotationNew = rotationChange.add(contentAnimation.startrotation);
        ['x', 'y', 'z'].forEach((key) => {
            this.animrotation[key] = rotationNew[key];
        });
        this.pattern.setAngleAxis(rotationNew);

        if (this.scaleAnimationAvailable) {
            const scaleChange = contentAnimation.scale.multiplyByFloats(perc, perc, perc);
            const scaleNew = scaleChange.add(contentAnimation.startscale);
            ['x', 'y', 'z'].forEach((key) => {
                this.animscale[key] = scaleNew[key];
            });
            this.pattern.setScaleAxis(scaleNew);
        }
        this._system.resetShadows();
    }

    private animateVector(contentAnimation: IAnimationOptions, vector: Vector3) {
        const xperc = Math.sign(vector.x) * contentAnimation.anim.easing.ease(Math.abs(vector.x));
        const yperc = Math.sign(vector.y) * contentAnimation.anim.easing.ease(Math.abs(vector.y));
        const zperc = Math.sign(vector.z) * contentAnimation.anim.easing.ease(Math.abs(vector.z));
        const percVector = new Vector3(xperc, yperc, zperc);

        const positionChange = contentAnimation.position.multiply(percVector);
        const positionNew = positionChange.add(this.animposition);
        this.pattern.setPosAxis(positionNew);

        // In order to follow mouse, rotation must invert x and y
        const rotationPercVector = new Vector3(yperc, -xperc, zperc);
        const rotationChange = contentAnimation.rotation.multiply(rotationPercVector);
        const rotationNew = rotationChange.add(this.animrotation);
        this.pattern.setAngleAxis(rotationNew);

        if (this.scaleAnimationAvailable) {
            const scaleChange = contentAnimation.scale.multiply(percVector);
            const scaleNew = scaleChange.add(this.animscale);
            this.pattern.setScaleAxis(scaleNew);
        }
        this._system.resetShadows();
    }

    // Different animateScroll function used in model
    public animateScroll(contentAnimation: IAnimationOptions, progress: number): void {
        this._animateScroll(contentAnimation, progress);
    }

    protected _animateScroll(contentAnimation: IAnimationOptions, progress: number): void {
        if (progress !== null) {
            if (!contentAnimation.startposition) this.setStartParam(contentAnimation);
            this.animate(contentAnimation, progress);
        }
    }

    // Different animateMouse function used in model
    public animateMouse(contentAnimation: IAnimationOptions, x: number, y: number): void {
        this._animateMouse(contentAnimation, x, y);
    }

    protected _animateMouse(contentAnimation: IAnimationOptions, x: number, y: number): void {
        this.animateVector(contentAnimation, new Vector3(x, y, 0));
    }

    public finished = false;

    private finishAnimation(contentAnimation: IAnimationOptions) {
        this.finished = true;
        const posFinal = contentAnimation.position.add(this.position);
        this.pattern.setPosAxis(posFinal);
        const scaleFinal = contentAnimation.scale.add(this.scale);
        this.pattern.setScaleAxis(scaleFinal);
        const rotationFinal = contentAnimation.rotation.add(this.rotation);
        this.pattern.setAngleAxis(rotationFinal);
    }

    public hide(): void {
        this._hideMove();
    }

    public show(): void {
        this._showMove();
    }

    protected _showMove(): void {
        this.resetGeometry();
        if (this.play) this.startAllAnimation();
        this._show();
    }

    protected _hideMove(): void {
        this.pauseAnimation();
        this._hide();
    }
}
